import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Plus, Loader2, LogOut } from "lucide-react";
import { argus, aErr, Btn, Label, inputCls, ARGUS_TOKEN } from "@/components/argus/ui";
import ArgusLogin from "@/components/argus/ArgusLogin";
import WhipLanding from "@/components/argus/WhipLanding";
import WhipReport from "@/components/argus/WhipReport";
import { ArgusLegalFooter } from "@/pages/ArgusLegal";

export default function ArgusWhip() {
  const [user, setUser] = useState(undefined); const [me, setMe] = useState(null); const [login, setLogin] = useState(false);
  const [reports, setReports] = useState([]); const [active, setActive] = useState(null); const [title, setTitle] = useState(""); const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "Argus WHIP · Fraud whistleblower report builder";
    if (!localStorage.getItem(ARGUS_TOKEN)) { setUser(null); return; }
    argus.get("/auth/me").then(r => setUser(r.data)).catch(() => { localStorage.removeItem(ARGUS_TOKEN); setUser(null); });
  }, []);
  useEffect(() => {
    if (user === undefined) return;
    if (!user) { setMe({ active: false }); return; }
    argus.get("/whip/me").then(r => {
      setMe(r.data);
      if (r.data.active) argus.get("/whip/reports").then(x => { setReports(x.data.reports); setActive(x.data.reports[0]?.report_id || null); });
    }).catch(() => setMe({ active: false }));
  }, [user]);

  const logout = () => { localStorage.removeItem(ARGUS_TOKEN); setUser(null); setReports([]); setActive(null); };
  const create = async () => {
    setErr("");
    try { const { data } = await argus.post("/whip/reports", { title: title.trim() }); setReports(r => [data, ...r]); setActive(data.report_id); setTitle(""); }
    catch (e) { setErr(aErr(e)); }
  };

  return (
    <main className="min-h-screen argus-root argus-bg text-[#f0fdf4]" data-testid="whip-page">
      <header className="border-b border-white/10 px-3 sm:px-5 py-3 flex items-center gap-2 sm:gap-3">
        <Link to="/argus" className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#10b981]" /><span className="argus-display text-xl font-bold">Argus</span></Link>
        <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300 border border-amber-300/40 rounded px-1.5 py-0.5">WHIP</span>
        <div className="flex-1" />
        <Link to="/argus/lawyers" className="text-xs text-white/60 hover:text-amber-300 whitespace-nowrap" data-testid="whip-lawyers-link">Find a lawyer</Link>
        {user ? <>
          {me?.until && <span className="text-[11px] font-mono text-amber-300/80 hidden sm:inline" data-testid="whip-until">Access until {new Date(me.until).toLocaleDateString()}</span>}
          <span className="text-[11px] text-white/50 hidden md:inline" data-testid="whip-user-email">{user.email}</span>
          <Btn kind="ghost" onClick={logout} className="whitespace-nowrap" data-testid="whip-logout-btn"><LogOut className="w-3.5 h-3.5" /><span className="hidden sm:inline">Log out</span></Btn>
        </> : user === null && <Btn onClick={() => setLogin(true)} data-testid="whip-signin-btn">Sign in</Btn>}
      </header>

      {!me ? <div className="p-10 text-white/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : !me.active ? <WhipLanding loggedIn={!!user} onSignIn={() => setLogin(true)} />
        : (
          <div className="grid md:grid-cols-[260px_1fr] min-h-[calc(100vh-53px)]">
            <aside className="border-r border-white/10 p-4 space-y-3" data-testid="whip-sidebar">
              <div className="flex gap-2">
                <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => e.key === "Enter" && title.trim().length > 1 && create()} placeholder="New fraud report" data-testid="whip-new-title" />
                <Btn onClick={create} disabled={title.trim().length < 2} data-testid="whip-new-btn"><Plus className="w-3.5 h-3.5" /></Btn>
              </div>
              {err && <div className="text-red-300 text-xs">{err}</div>}
              <Label>Your reports</Label>
              {reports.length === 0 && <div className="text-xs text-white/40">No reports yet. Start one above.</div>}
              {reports.map(r => (
                <button key={r.report_id} type="button" onClick={() => setActive(r.report_id)} className={`w-full text-left px-3 py-2 rounded-md text-sm font-semibold truncate ${active === r.report_id ? "bg-amber-300/10 border border-amber-300/40" : "hover:bg-white/5 border border-transparent"}`} data-testid={`whip-report-item-${r.report_id}`}>{r.title}</button>
              ))}
            </aside>
            <section className="min-w-0">
              {active ? <WhipReport key={active} rid={active}
                onUpdated={d => setReports(rs => rs.map(x => x.report_id === d.report_id ? { ...x, title: d.title } : x))}
                onDeleted={id => { const rest = reports.filter(x => x.report_id !== id); setReports(rest); setActive(rest[0]?.report_id || null); }} />
                : <div className="p-10 text-white/45 argus-display text-xl">Start a fraud report to begin.</div>}
            </section>
          </div>
        )}
      {login && <ArgusLogin onClose={() => setLogin(false)} onDone={u => { setLogin(false); setMe(null); setUser(u); }} />}
      <ArgusLegalFooter />
    </main>
  );
}
